import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../page_header";
import { guardianAlertService } from "../../../services/guardianAlertService";
import {
  classifyAlertType,
  getInitialPageForState,
  pickAlertId,
  resolveAlertResponseState,
} from "../../../utils/alertResponseState";
import {
  PageShell,
  Intro,
  DetailCard,
  FormSection,
  Field,
  Label,
  Select,
  TextArea,
  CheckList,
  CheckItem,
  CheckboxRow,
  ActionsRow,
  PrimaryButton,
  GhostButton,
  ErrorText,
  LoadingText,
  FileRow,
} from "../styles";

const FIRE_ACTIONS = [
  "Brigada local acionada",
  "Corpo de Bombeiros acionado",
  "Equipe própria enviada ao local",
  "Monitoramento contínuo da área",
  "Isolamento / contenção iniciados",
];

const DEFOREST_PROCESSES = [
  { key: "natural", label: "Processo natural" },
  { key: "antropico", label: "Processo antrópico" },
  { key: "intencional", label: "Processo intencional" },
];

/**
 * Fluxo multipágina de resposta:
 * 1) equipe_enviada
 * 2) respondido_processo_sucessao (+ anexos)
 */
export default function RespondAlert() {
  const { alertId: alertIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const preview = location.state?.alertPreview || null;
  const viewOnly = Boolean(location.state?.viewOnly);

  const alertId = String(alertIdParam || pickAlertId(preview) || "");

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [responseState, setResponseState] = useState(
    location.state?.responseState || null
  );
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  /** Membros da área (GuardianArea.other_responsibles) vindos da API. */
  const [areaTeam, setAreaTeam] = useState([]);

  // página 1
  const [equipeOk, setEquipeOk] = useState(false);
  const [selectedResponsibleIds, setSelectedResponsibleIds] = useState([]);
  const [includeOutro, setIncludeOutro] = useState(false);
  const [otherResponsibleText, setOtherResponsibleText] = useState("");

  // página 2
  const [fireChecks, setFireChecks] = useState({});
  const [deforestChecks, setDeforestChecks] = useState({});
  const [otherChecks, setOtherChecks] = useState({});
  const [observations, setObservations] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoType, setPhotoType] = useState("natural");
  const [pdfFile, setPdfFile] = useState(null);

  const alertTypeKind = useMemo(
    () => classifyAlertType(preview?.alert_type_description),
    [preview]
  );

  const responsibleOptions = useMemo(() => {
    const fromPreview =
      preview?.other_responsibles || preview?.other_responsibles_detail || [];
    const list = areaTeam.length > 0 ? areaTeam : fromPreview;
    if (!Array.isArray(list)) return [];
    return list
      .map((u) => {
        if (typeof u === "object") {
          return {
            id: String(u.id),
            label: u.full_name || u.name || `Usuário #${u.id}`,
          };
        }
        return { id: String(u), label: `Usuário #${u}` };
      })
      .filter((u) => u.id != null && u.id !== "");
  }, [areaTeam, preview]);

  const toggleResponsible = (id) => {
    const key = String(id);
    setSelectedResponsibleIds((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!alertId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // get_tickets_from_same_alert traz tickets + area_responsibles.other_responsibles
        const data = await guardianAlertService.getTicketsFromSameAlert(alertId);
        if (cancelled) return;

        const tickets = Array.isArray(data?.tickets)
          ? data.tickets
          : Array.isArray(data)
            ? data
            : [];
        const others =
          data?.area_responsibles?.other_responsibles ||
          data?.other_responsibles ||
          [];
        setAreaTeam(Array.isArray(others) ? others.filter(Boolean) : []);

        const state = resolveAlertResponseState(tickets);
        setResponseState(state);
        setPage(viewOnly ? 2 : getInitialPageForState(state));
        if (state === "already_answered" && !viewOnly) {
          setPage(2);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Não foi possível carregar o estado da resposta.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [alertId, viewOnly]);

  const submitEquipe = async ({ goNext }) => {
    if (!equipeOk) {
      setError("Confirme que enviará uma equipe ao local.");
      return;
    }
    if (includeOutro && !otherResponsibleText.trim()) {
      setError("Descreva quem mais foi enviado ou desmarque “Outro”.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const responsibleUserIds = selectedResponsibleIds
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n) && n > 0);

      let observationsText = "nenhuma";
      if (includeOutro) {
        observationsText = `Outro: ${otherResponsibleText.trim()}`;
      }

      await guardianAlertService.createFieldAlertTicket({
        alertId,
        action: "equipe_enviada",
        responses: "nenhuma",
        observations: observationsText,
        responsibleUserIds,
      });

      setResponseState("needs_resposta");
      if (goNext) setPage(2);
      else navigate("/my-alerts");
    } catch (err) {
      if (err?.response?.status === 409) {
        setPage(2);
      } else {
        setError(
          err?.response?.data?.detail ||
            err?.response?.data?.error ||
            "Não foi possível registrar o envio da equipe."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const buildFinalResponses = () => {
    if (alertTypeKind === "incendio") {
      const selected = FIRE_ACTIONS.filter((label) => fireChecks[label]);
      return selected.join("; ") || "nenhuma";
    }
    if (alertTypeKind === "desmatamento") {
      const parts = DEFOREST_PROCESSES.filter((p) => deforestChecks[p.key]).map(
        (p) => p.label
      );
      return parts.length ? parts.join("; ") : "nenhuma";
    }
    const parts = DEFOREST_PROCESSES.filter((p) => otherChecks[p.key]).map(
      (p) => p.label
    );
    return parts.length ? parts.join("; ") : "nenhuma";
  };

  const submitFinal = async () => {
    const responses = buildFinalResponses();
    if (responses === "nenhuma") {
      setError("Selecione ao menos uma providência / processo.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const attachments = [];
      if (photoFile) {
        attachments.push({ file: photoFile, tipo: photoType });
      }
      if (pdfFile) {
        attachments.push({ file: pdfFile, tipo: "documento" });
      }

      await guardianAlertService.createFieldAlertTicket({
        alertId,
        action: "respondido_processo_sucessao",
        responses,
        observations: observations.trim(),
        attachments,
      });

      navigate("/my-alerts", { replace: true });
    } catch (err) {
      if (err?.response?.status === 409) {
        setError("Resposta já registrada para este alerta.");
      } else {
        setError(
          err?.response?.data?.detail ||
            err?.response?.data?.error ||
            "Não foi possível enviar a resposta final."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageShell $roomy>
        <PageHeader
          title="Responder alerta"
          actionLabel="Voltar"
          onAction={() => navigate(`/my-alerts/${alertId}`)}
        />
        <LoadingText>Carregando…</LoadingText>
      </PageShell>
    );
  }

  return (
    <PageShell $roomy>
      <PageHeader
        title={page === 1 ? "Envio de equipe" : "Resposta final"}
        actionLabel="Voltar"
        onAction={() =>
          page === 2 && responseState === "needs_resposta"
            ? setPage(1)
            : navigate(`/my-alerts/${alertId}`)
        }
      />
      <Intro>
        {page === 1
          ? "Informe que uma equipe foi (ou será) enviada ao local do alerta."
          : "Registre as providências tomadas e, se quiser, anexe fotos ou documentos."}
      </Intro>

      <DetailCard>
        {page === 1 ? (
          <FormSection>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={equipeOk}
                onChange={(e) => setEquipeOk(e.target.checked)}
                disabled={viewOnly || submitting}
              />
              <span>
                Recebi a informação e vou enviar / enviei uma equipe para
                verificar a ocorrência.
              </span>
            </CheckboxRow>

            <Field>
              <Label>Membros da equipe enviados (opcional — pode marcar vários)</Label>
              {responsibleOptions.length === 0 ? (
                <LoadingText style={{ margin: 0 }}>
                  Nenhum membro cadastrado nesta área (other_responsibles / papel TEAM).
                </LoadingText>
              ) : (
                <CheckList>
                  {responsibleOptions.map((u) => (
                    <CheckItem key={u.id}>
                      <input
                        type="checkbox"
                        checked={selectedResponsibleIds.includes(u.id)}
                        onChange={() => toggleResponsible(u.id)}
                        disabled={viewOnly || submitting}
                      />
                      <span>{u.label}</span>
                    </CheckItem>
                  ))}
                </CheckList>
              )}
              <CheckItem style={{ marginTop: "0.5rem" }}>
                <input
                  type="checkbox"
                  checked={includeOutro}
                  onChange={(e) => {
                    setIncludeOutro(e.target.checked);
                    if (!e.target.checked) setOtherResponsibleText("");
                  }}
                  disabled={viewOnly || submitting}
                />
                <span>Outro (não listado)</span>
              </CheckItem>
            </Field>

            {includeOutro ? (
              <Field>
                <Label>Descreva quem mais foi enviado</Label>
                <TextArea
                  value={otherResponsibleText}
                  onChange={(e) => setOtherResponsibleText(e.target.value)}
                  disabled={viewOnly || submitting}
                />
              </Field>
            ) : null}

            {error ? <ErrorText>{error}</ErrorText> : null}

            <ActionsRow>
              <GhostButton
                type="button"
                disabled={submitting || viewOnly}
                onClick={() => submitEquipe({ goNext: false })}
              >
                Responder depois
              </GhostButton>
              <PrimaryButton
                type="button"
                disabled={submitting || viewOnly}
                onClick={() => submitEquipe({ goNext: true })}
              >
                {submitting ? "Salvando…" : "Continuar para resposta"}
              </PrimaryButton>
            </ActionsRow>
          </FormSection>
        ) : (
          <FormSection>
            {alertTypeKind === "incendio" ? (
              <Field>
                <Label>Providências (incêndio)</Label>
                <CheckList>
                  {FIRE_ACTIONS.map((label) => (
                    <CheckItem key={label}>
                      <input
                        type="checkbox"
                        checked={Boolean(fireChecks[label])}
                        disabled={viewOnly || submitting}
                        onChange={(e) =>
                          setFireChecks((prev) => ({
                            ...prev,
                            [label]: e.target.checked,
                          }))
                        }
                      />
                      {label}
                    </CheckItem>
                  ))}
                </CheckList>
              </Field>
            ) : (
              <Field>
                <Label>
                  {alertTypeKind === "desmatamento"
                    ? "Processos identificados"
                    : "Classificação da ocorrência"}
                </Label>
                <CheckList>
                  {DEFOREST_PROCESSES.map((p) => (
                    <CheckItem key={p.key}>
                      <input
                        type="checkbox"
                        checked={Boolean(
                          (alertTypeKind === "desmatamento"
                            ? deforestChecks
                            : otherChecks)[p.key]
                        )}
                        disabled={viewOnly || submitting}
                        onChange={(e) => {
                          const setter =
                            alertTypeKind === "desmatamento"
                              ? setDeforestChecks
                              : setOtherChecks;
                          setter((prev) => ({
                            ...prev,
                            [p.key]: e.target.checked,
                          }));
                        }}
                      />
                      {p.label}
                    </CheckItem>
                  ))}
                </CheckList>
              </Field>
            )}

            <Field>
              <Label>Observações</Label>
              <TextArea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                disabled={viewOnly || submitting}
                placeholder="Detalhes adicionais (opcional)"
              />
            </Field>

            <Field>
              <Label>Foto (opcional)</Label>
              <FileRow>
                <Select
                  value={photoType}
                  onChange={(e) => setPhotoType(e.target.value)}
                  disabled={viewOnly || submitting || !photoFile}
                  style={{ maxWidth: 180 }}
                >
                  <option value="natural">Natural</option>
                  <option value="antropico">Antrópico</option>
                  <option value="intencional">Intencional</option>
                </Select>
                <input
                  type="file"
                  accept="image/*"
                  disabled={viewOnly || submitting}
                  onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                />
              </FileRow>
            </Field>

            <Field>
              <Label>Documento PDF/DOC (opcional)</Label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,application/pdf"
                disabled={viewOnly || submitting}
                onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
              />
            </Field>

            {error ? <ErrorText>{error}</ErrorText> : null}

            <ActionsRow>
              <GhostButton
                type="button"
                onClick={() => navigate("/my-alerts")}
                disabled={submitting}
              >
                Cancelar
              </GhostButton>
              {!viewOnly && responseState !== "already_answered" ? (
                <PrimaryButton
                  type="button"
                  disabled={submitting}
                  onClick={submitFinal}
                >
                  {submitting ? "Enviando…" : "Enviar resposta final"}
                </PrimaryButton>
              ) : null}
            </ActionsRow>
          </FormSection>
        )}
      </DetailCard>
    </PageShell>
  );
}
